import {
    Box,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
    Slider,
} from '@mui/material';
import { useRecoilState } from 'recoil';
import { Info } from '@mui/icons-material';
import { FormattedMessage } from 'react-intl';
import { brushOptionsState } from '~/state/atoms/toolsAtoms';
import { cmToMm, mmToCm } from '~/utils/units';
import { BrushTool } from '~/engine/tool/tools/contouring-tools/BrushTool';

export default function BrushToolDialog() {
    const [brushOptions, setBrushOptions] = useRecoilState(brushOptionsState);

    const handleOperationChange = (
        _: React.MouseEvent<HTMLElement, MouseEvent>,
        newValue: 'static' | 'smart' | null
    ) => {
        if (newValue !== null && newValue !== brushOptions.mode) {
            setBrushOptions((options) => ({
                ...options,
                mode: newValue,
            }));
        }
    };

    const handleEraseToggle = (
        _: React.MouseEvent<HTMLElement, MouseEvent>,
        newValue: boolean
    ) => {
        if (newValue !== null && newValue !== brushOptions.erase) {
            setBrushOptions((options) => ({ ...options, erase: newValue }));
        }
    };

    const handleSetBrushSize = (_: Event, newValue: number | number[]) => {
        const newDiameter = cmToMm(
            Array.isArray(newValue) ? newValue[0] : newValue
        );

        if (newDiameter < 0.1 || newDiameter > BrushTool.MAX_BRUSH_SIZE) {
            return;
        }
        setBrushOptions((options) => ({
            ...options,
            diameter: newDiameter,
        }));
    };

    return (
        <Box display="flex" flexDirection="column" gap={1}>
            <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h6">
                    <FormattedMessage id="brushTool" />
                </Typography>
                <Tooltip title={<FormattedMessage id="brushToolGuide" />}>
                    <Info fontSize="small" />
                </Tooltip>
            </Stack>
            <Box display="flex" flexDirection="column" gap={2}>
                <ToggleButtonGroup
                    color="primary"
                    value={brushOptions.mode}
                    exclusive
                    onChange={handleOperationChange}
                    aria-label="Brush Mode"
                    fullWidth
                    size="small"
                >
                    <Tooltip
                        title={<FormattedMessage id="brushStaticTooltip" />}
                    >
                        <ToggleButton size="small" value={'static'}>
                            static
                        </ToggleButton>
                    </Tooltip>
                    <Tooltip
                        title={<FormattedMessage id="brushSmartTooltip" />}
                    >
                        <ToggleButton size="small" value={'smart'}>
                            smart
                        </ToggleButton>
                    </Tooltip>
                </ToggleButtonGroup>
                <ToggleButtonGroup
                    color="primary"
                    value={brushOptions.erase}
                    exclusive
                    onChange={handleEraseToggle}
                    aria-label="Brush Action"
                    fullWidth
                    size="small"
                >
                    <ToggleButton value={false}>Draw</ToggleButton>
                    <ToggleButton value={true}>Erase</ToggleButton>
                </ToggleButtonGroup>
                <Box>
                    <Typography gutterBottom>
                        <FormattedMessage id="brushWidth" />:{' '}
                        {mmToCm(brushOptions.diameter).toFixed(1)}
                    </Typography>
                    <Slider
                        value={mmToCm(brushOptions.diameter)}
                        onChange={handleSetBrushSize}
                        step={0.1}
                        min={0.2}
                        max={4.0}
                        aria-labelledby="brush-diameter-slider"
                    />
                </Box>
            </Box>
        </Box>
    );
}
